import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocationNormalized, RouteLocationRaw, RouteRecordRaw } from 'vue-router'
import HomePage from '@/views/HomePage.vue'
import LoginView from '@/views/LoginView.vue'
import ForbiddenView from '@/views/errors/ForbiddenView.vue'
import { useUnifiedPermission } from '@/composables/useUnifiedPermission'
import { hasAdminRole } from '@/types/auth'
import { PERMISSION_CODES } from '@/utils/permissions'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: HomePage,
    meta: { public: true },
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { public: true },
  },
  {
    path: '/auth/wechat/callback',
    name: 'wechat-login-callback',
    component: () => import('@/views/auth/WechatLoginCallbackView.vue'),
    meta: { public: true },
  },
  {
    path: '/auth/wechat/bind-callback',
    name: 'wechat-bind-callback',
    component: () => import('@/views/auth/WechatBindCallbackView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/service-agreement',
    name: 'service-agreement',
    component: () => import('@/views/legal/ServiceAgreementView.vue'),
    meta: { public: true },
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: () => import('@/views/legal/PrivacyPolicyView.vue'),
    meta: { public: true },
  },
  {
    path: '/setup',
    name: 'setup',
    component: () => import('@/views/QuickSetupView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/docs',
    name: 'docs-index',
    component: () => import('@/views/docs/DocLayout.vue'),
    meta: { public: true },
    children: [
      {
        path: '',
        name: 'docs-home',
        component: () => import('@/views/docs/DocIndexView.vue'),
        meta: { public: true },
      },
      {
        path: ':docId',
        name: 'docs-content',
        component: () => import('@/views/docs/DocContentView.vue'),
        meta: { public: true },
      },
    ],
  },
  {
    path: '/news',
    redirect: '/',
  },
  {
    path: '/news/:id',
    name: 'news-detail',
    component: () => import('@/views/news/NewsDetailView.vue'),
    meta: { public: true },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.WORKSPACE_DASHBOARD },
  },
  {
    path: '/admin',
    redirect: '/admin/overview',
  },
  {
    path: '/admin/overview',
    name: 'admin-overview',
    component: () => import('@/views/admin/SystemOverviewView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_OVERVIEW },
  },
  // Admin sub-routes
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('@/views/admin/UserManageView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_OVERVIEW },
  },
  {
    path: '/admin/users/:id',
    name: 'admin-user-detail',
    component: () => import('@/views/admin/UserDetailView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_OVERVIEW },
  },
  // 【修复角色权限三审#1/#4】后端端点已由 hasRole('SUPER_ADMIN') 收口到 admin:role:* 权限码，
  // 路由不再写死 superAdminOnly，门控统一走 meta.permission（与端点实发码同源）；
  // 原 data-scope 路由随「数据权限」整域删除（后端端点与 role_data_scopes 表已移除）。
  {
    path: '/admin/roles',
    name: 'admin-roles',
    component: () => import('@/views/admin/role-permission/RoleListView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_ROLE_VIEW },
  },
  {
    path: '/admin/roles/:id',
    name: 'admin-role-detail',
    component: () => import('@/views/admin/role-permission/RoleDetailView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_ROLE_VIEW },
  },
  {
    path: '/admin/roles/:id/permissions',
    name: 'admin-role-permissions',
    component: () => import('@/views/admin/role-permission/PermissionEditView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_ROLE_PERMISSION },
  },
  {
    path: '/admin/role-permission/templates',
    name: 'admin-role-templates',
    component: () => import('@/views/admin/role-permission/PermissionTemplateView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_ROLE_VIEW },
  },
  // 统一审计日志：六类变更留痕（文档/系统配置/首页内容/单位/角色/用户）合并为一页，
  // 原 /admin/role-permission/logs、/admin/org-logs、/admin/landing-page/audit-logs、
  // /admin/system-config/audit-logs、/document/audit 五条路由与僵尸路由 /admin/logs 一并删除
  {
    path: '/admin/audit-logs',
    name: 'admin-audit-logs',
    component: () => import('@/views/admin/audit-logs/AuditCenterView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_AUDIT_VIEW },
  },
  // 首页内容管理路由
  {
    path: '/admin/landing-page',
    redirect: '/admin/landing-page/sections',
  },
  {
    path: '/admin/landing-page/sections',
    name: 'admin-landing-sections',
    component: () => import('@/views/admin/LandingPageManageView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
  },
  {
    path: '/admin/landing-page/hero',
    name: 'admin-landing-hero',
    component: () => import('@/views/admin/landing/HeroContentEdit.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
  },
  {
    path: '/admin/landing-page/features',
    name: 'admin-landing-features',
    component: () => import('@/views/admin/landing/FeatureCardManage.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
  },
  {
    path: '/admin/landing-page/stats',
    name: 'admin-landing-stats',
    component: () => import('@/views/admin/landing/DataStatManage.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
  },
  {
    path: '/admin/landing-page/testimonials',
    name: 'admin-landing-testimonials',
    component: () => import('@/views/admin/landing/TestimonialManage.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
  },
  {
    path: '/admin/landing-page/docs-news',
    name: 'admin-landing-docs-news',
    component: () => import('@/views/admin/landing/DocNewsManage.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
  },
  {
    path: '/admin/landing-page/process',
    name: 'admin-landing-process',
    component: () => import('@/views/admin/landing/ProcessStepEdit.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
  },
  {
    path: '/admin/landing-page/scenarios',
    name: 'admin-landing-scenarios',
    component: () => import('@/views/admin/landing/ScenarioManage.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
  },
  {
    path: '/admin/landing-page/kanban',
    name: 'admin-landing-kanban',
    component: () => import('@/views/admin/landing/KanbanPreviewManage.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
  },
  {
    path: '/admin/landing-page/cta-footer',
    name: 'admin-landing-cta-footer',
    component: () => import('@/views/admin/landing/CtaFooterManage.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
  },
  {
    path: '/admin/landing-page/seo',
    name: 'admin-landing-seo',
    component: () => import('@/views/admin/landing/SeoMetaManage.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
  },
  {
    path: '/admin/settings',
    redirect: '/admin/system-config/basic',
  },
  // 系统消息路由（细粒度权限码 admin:message，与后端权限目录对齐）
  {
    path: '/admin/system-messages/announcements',
    name: 'admin-system-messages-announcements',
    component: () => import('@/views/admin/system-messages/AnnouncementView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_MESSAGE },
  },
  {
    path: '/admin/system-messages/announcements/:id',
    name: 'admin-system-messages-announcement-detail',
    component: () => import('@/views/admin/system-messages/AnnouncementDetail.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_MESSAGE },
  },
  {
    path: '/admin/system-messages/feedbacks',
    name: 'admin-system-messages-feedbacks',
    component: () => import('@/views/admin/system-messages/FeedbackListView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_MESSAGE },
  },
  {
    path: '/admin/system-messages/feedbacks/:id',
    name: 'admin-system-messages-feedback-detail',
    component: () => import('@/views/admin/system-messages/FeedbackDetail.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_MESSAGE },
  },
  {
    path: '/admin/system-messages/templates',
    name: 'admin-system-messages-templates',
    component: () => import('@/views/admin/system-messages/TemplateManageView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_MESSAGE },
  },
  {
    path: '/admin/system-messages/send-logs',
    name: 'admin-system-messages-send-logs',
    component: () => import('@/views/admin/system-messages/SendLogView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_MESSAGE },
  },
  // 系统配置路由
  // 修复配置审查#12（口径②）：meta 由字面量 'admin:overview' 改为 ADMIN_CONFIG_READ——
  // 后端 8 个读端点全部 @PreAuthorize("hasAuthority('admin:config:read')")，而 admin:overview
  // 只是「进入管理区」的总闸码，两者不同物；写权限按子模块细码在各 View 的按钮上门控。
  // 发票配置路由随该整域下线（只存不用，全后端无开票业务读取）
  {
    path: '/admin/system-config/basic',
    name: 'admin-system-config-basic',
    component: () => import('@/views/admin/system-config/BasicSettingsView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_CONFIG_READ },
  },
  {
    path: '/admin/system-config/ai-models',
    name: 'admin-system-config-ai-models',
    component: () => import('@/views/admin/system-config/AIModelConfigView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_CONFIG_READ },
  },
  {
    path: '/admin/system-config/payment',
    name: 'admin-system-config-payment',
    component: () => import('@/views/admin/system-config/PaymentConfigView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_CONFIG_READ },
  },
  {
    path: '/admin/system-config/security',
    name: 'admin-system-config-security',
    component: () => import('@/views/admin/system-config/SecurityPolicyView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_CONFIG_READ },
  },
  {
    path: '/admin/system-config/notification',
    name: 'admin-system-config-notification',
    component: () => import('@/views/admin/system-config/NotificationConfigView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_CONFIG_READ },
  },
  {
    path: '/admin/system-config/legal',
    name: 'admin-system-config-legal',
    component: () => import('@/views/admin/system-config/LegalInfoView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_CONFIG_READ },
  },
  {
    path: '/admin/system-config/archive-rules',
    name: 'admin-system-config-archive-rules',
    component: () => import('@/views/admin/system-config/ArchiveRuleConfigView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_CONFIG_READ },
  },
  // 账号安全（当前登录管理员自己的改密页）：刻意不带 meta.permission——
  // 改自己密码不属于「配置读权限」，上面 7 条子页要 ADMIN_CONFIG_READ 是因为它们读写系统配置；
  // 本页可入性由 beforeEachGuard 的 /admin 总闸（hasAdminRole）把关，与「敏感词库」同先例。
  // 叠码会造出「持管理后台角色但无配置码的管理员改不了自己密码、后端却只要求 authenticated」的分裂。
  {
    path: '/admin/system-config/account',
    name: 'admin-system-config-account',
    component: () => import('@/views/admin/system-config/AccountSecurityView.vue'),
    meta: { requiresAuth: true },
  },
  // 套餐交费管理路由
  {
    path: '/admin/plans',
    name: 'admin-plans',
    component: () => import('@/views/admin/PlanManageView.vue'),
    // 修复 P3#27：路由补 meta.permission（与项目其他路由保持单数字符串约定）
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_PLAN_VIEW },
  },
  {
    // 修复计费审查#P1-2：新建页与编辑页分列两条路由——原 /admin/plans/:id 单一守卫用
    // ADMIN_PLAN_EDIT，而 PlanManageView 的"新建"按钮门控 ADMIN_PLAN_CREATE 并跳 /admin/plans/new，
    // 导致仅持 create 不持 edit 的角色点得到却进不去（admin 双码兼具掩盖）。新建守卫 CREATE，编辑守卫 EDIT。
    path: '/admin/plans/new',
    name: 'admin-plan-create',
    component: () => import('@/views/admin/PlanDetailView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_PLAN_CREATE },
  },
  {
    path: '/admin/plans/:id',
    name: 'admin-plan-detail',
    component: () => import('@/views/admin/PlanDetailView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_PLAN_EDIT },
  },
  {
    path: '/admin/orders',
    name: 'admin-orders',
    component: () => import('@/views/admin/OrderManageView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_ORDER_VIEW },
  },
  {
    path: '/admin/subscriptions',
    name: 'admin-subscriptions',
    component: () => import('@/views/admin/SubscriptionManageView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_SUBSCRIPTION_VIEW },
  },
  {
    path: '/admin/plan-stats',
    name: 'admin-plan-stats',
    component: () => import('@/views/admin/PlanStatsView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_PLAN_STATS_VIEW },
  },
  // 用户侧套餐路由（已整合至账号设置-套餐管理，保留重定向）
  {
    path: '/subscription',
    redirect: (to) => ({ path: '/settings/subscription', query: { ...to.query, tab: 'plans' } }),
  },
  {
    path: '/subscription/orders',
    redirect: (to) => ({ path: '/settings/subscription', query: { ...to.query, tab: 'orders' } }),
  },
  // TASK-006 真实支付页面
  {
    path: '/payment/checkout',
    name: 'payment-checkout',
    component: () => import('@/views/payment/CheckoutView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/payment/result',
    name: 'payment-result',
    component: () => import('@/views/payment/PaymentResultView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/templates',
    name: 'admin-templates',
    component: () => import('@/views/admin/TemplateManageView.vue'),
    // C6 配套：模板路由改用模板权限码（原先误挂 admin:overview）
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_TEMPLATE_VIEW },
  },
  {
    path: '/admin/presets',
    name: 'admin-presets',
    component: () => import('@/views/admin/ComponentPresetView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_TEMPLATE_VIEW },
  },
  // 单位管理路由（细粒度权限码：后端 RolePresetConfig 的 SYS_ADMIN 已授予 admin:org:view）
  {
    path: '/admin/organizations',
    name: 'admin-organizations',
    component: () => import('@/views/admin/OrganizationManageView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_ORG_VIEW },
  },
  {
    path: '/admin/organizations/:id',
    name: 'admin-organization-detail',
    component: () => import('@/views/admin/OrganizationDetailView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_ORG_VIEW },
  },
  {
    path: '/admin/org-applications',
    name: 'admin-org-applications',
    component: () => import('@/views/admin/OrganizationApplicationView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_ORG_APPROVE },
  },
  // 记录类型管理（全局预设，超级管理员）
  {
    path: '/admin/record-types/preset',
    name: 'admin-record-types-preset',
    component: () => import('@/views/admin/RecordTypePresetView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.ADMIN_RECORD_TYPE_PRESET },
  },
  // 单位管理员工作空间路由
  {
    path: '/workspace/members',
    name: 'workspace-members',
    component: () => import('@/views/workspace/OrgMemberListView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.WORKSPACE_DASHBOARD },
  },
  // 自定义记录类型管理（租户管理员）
  {
    path: '/workspace/record-types',
    name: 'workspace-record-types',
    component: () => import('@/views/workspace/RecordTypeCustomView.vue'),
    // 修复工作记录审查#6：页面门控从 WORKSPACE_DASHBOARD 换为本域实码 RECORD_TYPE_MANAGE，
    // 与后端 RecordTypeController 写端点及页内按钮同一口径（dashboard 码在此无裁决力）
    meta: { requiresAuth: true, permission: PERMISSION_CODES.RECORD_TYPE_MANAGE },
  },
  // 台账工作区（standard 主路由组）
  // 修复台账审查#12：四页补 permission 码门控，与后端 @PreAuthorize 同口径——
  // 此前仅 requiresAuth，无 workspace:ledger:view 的用户直输 URL 能打开页面再吃一串 403，
  // 与 028 起「菜单/路由/后端」三道门口径统一的既有范式不一致
  {
    path: '/ledgers',
    name: 'ledger-list',
    component: () => import('@/views/ledger/LedgerListView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.LEDGER_VIEW },
  },
  {
    path: '/ledgers/create',
    name: 'ledger-create',
    component: () => import('@/views/ledger/LedgerCreateView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.LEDGER_CREATE },
  },
  {
    path: '/ledgers/:ledgerId/tasks',
    name: 'ledger-tasks',
    component: () => import('@/views/task/TaskList.vue'),
    // 修复任务审查#2：与 GET /api/tasks 的 hasAuthority('workspace:task:view') 同码，
    // 无码用户此前可进页面、列表请求才 403
    meta: { requiresAuth: true, permission: PERMISSION_CODES.TASK_VIEW },
  },
  {
    path: '/ledgers/:id',
    name: 'ledger-detail',
    component: () => import('@/views/ledger/LedgerDetailView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.LEDGER_VIEW },
  },
  {
    path: '/ledgers/:id/edit',
    name: 'ledger-edit',
    component: () => import('@/views/ledger/LedgerEditView.vue'),
    // 编辑页写端点（台账更新/组件增删改）后端一律要求 ledger:edit，与角色兜底 manager+staff 同源
    meta: { requiresAuth: true, permission: PERMISSION_CODES.LEDGER_EDIT },
  },
  {
    path: '/ledgers/:id/preview',
    name: 'ledger-preview',
    component: () => import('@/views/ledger/LedgerPreviewView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.LEDGER_VIEW },
  },
  {
    // 台账全景仪表盘（FR-009）：三维指标 + 健康度 + 风险清单 + 跨台账排行
    path: '/ledgers/:id/dashboard',
    name: 'ledger-dashboard',
    component: () => import('@/views/ledger/LedgerProjectDashboard.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.LEDGER_VIEW },
  },
  {
    path: '/tasks',
    name: 'task-list',
    component: () => import('@/views/task/TaskList.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.TASK_VIEW },
  },
  {
    path: '/reports',
    name: 'report-list',
    component: () => import('@/views/report/ReportListView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.WORKSPACE_REPORT },
  },
  {
    path: '/reports/create',
    name: 'report-create',
    component: () => import('@/views/report/ReportCreateView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.WORKSPACE_REPORT_CREATE },
  },
  {
    path: '/reports/:id',
    name: 'report-detail',
    component: () => import('@/views/report/ReportDetailView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.WORKSPACE_REPORT },
  },
  // 管理员宣传报道路由：三道门（菜单/路由/后端）统一 super_admin 专属口径。
  // workspace:report 同时授予组织 leader/manager，仅靠 permission 挡不住组织角色直输 URL，
  // 故叠加 superAdminOnly 按角色把关（后端 adminStats/listReports 管理分支同为超管限定）。
  {
    path: '/admin/reports',
    name: 'admin-reports',
    component: () => import('@/views/report/AdminReportListView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.WORKSPACE_REPORT, superAdminOnly: true },
  },
  {
    path: '/admin/reports/:id',
    name: 'admin-report-detail',
    component: () => import('@/views/report/ReportDetailView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.WORKSPACE_REPORT, superAdminOnly: true },
  },
  {
    path: '/analysis',
    name: 'analysis',
    component: () => import('@/views/analysis/AnalysisOverviewView.vue'),
    // 智能分析全量修复：双码 any-of 与后端 @PreAuthorize/侧边栏同口径（此前两条路由仅
    // requiresAuth，无码用户 URL 直达靠后端 403 兜底）
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_CODES.WORKSPACE_ANALYSIS, PERMISSION_CODES.WORKSPACE_ANALYSIS_VIEW],
    },
  },
  {
    path: '/analysis/:ledgerId',
    name: 'analysis-detail',
    component: () => import('@/views/analysis/AnalysisDetailView.vue'),
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_CODES.WORKSPACE_ANALYSIS, PERMISSION_CODES.WORKSPACE_ANALYSIS_VIEW],
    },
  },
  {
    // 敏感词库管理（管理端）：后端 hasRole('ADMIN')，/admin 总闸已按角色把关，
    // 故不叠权限码——语义保持与后端一致
    path: '/admin/sensitive-words',
    name: 'admin-sensitive-words',
    component: () => import('@/views/admin/sensitive-words/SensitiveWordListView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/messages',
    name: 'messages',
    component: () => import('@/views/NotificationsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/user/settings/SettingsLayout.vue'),
    meta: { requiresAuth: true },
    redirect: '/settings/profile',
    children: [
      { path: 'profile', name: 'settings-profile', component: () => import('@/views/user/settings/ProfileView.vue') },
      { path: 'security', name: 'settings-security', component: () => import('@/views/user/settings/SecurityView.vue') },
      { path: 'organization', name: 'settings-organization', component: () => import('@/views/user/settings/OrganizationView.vue') },
      { path: 'subscription', name: 'settings-subscription', component: () => import('@/views/user/settings/SubscriptionSummaryView.vue') },
      { path: 'notifications', name: 'settings-notifications', component: () => import('@/views/user/settings/NotificationsView.vue') },
      { path: 'devices', name: 'settings-devices', component: () => import('@/views/user/settings/DevicesView.vue') },
      { path: 'preferences', name: 'settings-preferences', component: () => import('@/views/user/settings/PreferencesView.vue') },
      { path: 'help', name: 'settings-help', component: () => import('@/views/user/settings/HelpView.vue') },
      { path: 'danger', name: 'settings-danger', component: () => import('@/views/user/settings/DangerZoneView.vue') },
    ],
  },
  // 工作记录路由
  {
    path: '/inspections',
    name: 'inspection-calendar',
    component: () => import('@/views/inspection/InspectionCalendarView.vue'),
    // 修复迎检审查#4：补细粒度权限码，无 view 码角色 URL 直达跳 /403
    meta: { requiresAuth: true, permission: PERMISSION_CODES.INSPECTION_VIEW },
  },
  {
    path: '/records',
    name: 'record-list',
    component: () => import('@/views/record/RecordListView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/records/create',
    name: 'record-create',
    component: () => import('@/views/record/RecordCreateView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/records/:id',
    name: 'record-detail',
    component: () => import('@/views/record/RecordDetailView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/records/:id/edit',
    name: 'record-edit',
    component: () => import('@/views/record/RecordEditView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/records/recycle',
    name: 'record-recycle',
    component: () => import('@/views/record/RecordRecycleView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/records/stats',
    name: 'record-stats',
    component: () => import('@/views/record/RecordStatsView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.RECORD_VIEW },
  },
  // 文档中心路由
  {
    path: '/document',
    name: 'document-center',
    component: () => import('@/views/document/index.vue'),
    // 修复文档审查#13/#19：与侧栏菜单同码门控（此前仅 requiresAuth，无 doc:center 的
    // 自定义角色可 URL 直达并把列表请求打到要 doc:file:read 的读端点拿 403）
    meta: { requiresAuth: true, permission: PERMISSION_CODES.DOC_CENTER },
  },
  // 注：原 /document/preview/:fileId 路由已删除——PdfPreview.vue 因 pdfUrl prop 恒空
  // 是断链空壳，预览统一由 components/common/FilePreview.vue 弹窗承担
  // 注：原 /document/recycle 与 /document/favorites 路由已删除——两页功能完整但
  // 全应用无导航入口（回收站经 FolderTree 虚拟节点、收藏经列表星标列在文档中心主页内联实现），
  // 保留会造成双实现漂移，故移除孤页路由与视图
  {
    path: '/document/versions/:fileId',
    name: 'document-versions',
    component: () => import('@/views/document/version/VersionHistory.vue'),
    // 修复文档审查#13：版本列表端点已按 doc:file:read 鉴权，路由同码门控
    meta: { requiresAuth: true, permission: PERMISSION_CODES.DOC_FILE_READ },
  },
  {
    path: '/document/knowledge',
    name: 'document-knowledge',
    component: () => import('@/views/document/knowledge/ChatView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.DOC_KB_CHAT },
  },
  {
    path: '/document/sync-status',
    name: 'document-sync-status',
    component: () => import('@/views/document/knowledge/SyncStatusView.vue'),
    // 修复文档审查#13：真缺口——此前无 permission，无同步码的人 URL 直达仍能取到
    // GET /sync/status 与 /sync/overview 的数据（按钮虽禁用但数据照回）
    meta: { requiresAuth: true, permission: PERMISSION_CODES.DOC_KB_SYNC },
  },
  {
    path: '/document/stats',
    name: 'document-stats',
    component: () => import('@/views/document/stats/DocumentStatsView.vue'),
    meta: { requiresAuth: true, permission: PERMISSION_CODES.DOC_STATS_VIEW },
  },
  {
    path: '/document/watermark-config',
    name: 'document-watermark-config',
    component: () => import('@/views/document/security/WatermarkConfig.vue'),
    // 修复文档审查#3：门控码与 WatermarkConfigController 实发码同源
    meta: { requiresAuth: true, permission: PERMISSION_CODES.DOC_WATERMARK_CONFIG },
  },
  // 403 无权限页面
  {
    path: '/403',
    name: 'forbidden',
    component: ForbiddenView,
    meta: { public: true },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach(beforeEachGuard)

// 发版后旧 chunk 失效兜底：动态导入的懒加载模块 404 时，强制刷新一次加载新版本入口
const ROUTE_CHUNK_RELOAD_KEY = 'route_chunk_reloaded'

router.onError((error: unknown, to) => {
  const message = error instanceof Error ? error.message : String(error)
  const isChunkLoadError =
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  if (!isChunkLoadError) {
    return
  }

  // 防止刷新循环：同一目标路由只自动刷新一次
  const reloadKey = 'route_chunk_reloaded'
  const lastReloadedPath = sessionStorage.getItem(reloadKey)
  const targetPath = to?.fullPath ?? window.location.pathname
  if (lastReloadedPath === targetPath) {
    return
  }
  sessionStorage.setItem(reloadKey, targetPath)
  // 整页刷新会重新请求 index.html，从而加载新版本入口和 chunk
  window.location.assign(targetPath)
})

// 导航成功后清除刷新标记：同一目标路由在下次发版后仍可兜底刷新一次
router.afterEach(() => {
  sessionStorage.removeItem(ROUTE_CHUNK_RELOAD_KEY)
})

export default router

/**
 * 全局路由守卫（导出供单元测试调用）。
 * 内部使用 useUnifiedPermission 与 hasAdminRole 判定，7 条分支：public 放行、requiresAuth、admin 跳过 /setup、
 * /admin/** 总闸（非 admin/super_admin 一律 403）、superAdminOnly 仅超管、super_admin 短路、
 * meta.permission 检查失败跳 /403。
 */
export function beforeEachGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): boolean | RouteLocationRaw {
  const { state } = useUnifiedPermission()
  const currentUser = state.currentUser

  // Public routes: allow all
  if (to.meta.public) {
    // Redirect authenticated users away from login
    if (to.path === '/login' && state.isAuthenticated) {
      return hasAdminRole(currentUser?.role) ? '/admin' : '/dashboard'
    }
    return true
  }

  // Protected routes: require authentication
  if (to.meta.requiresAuth && !state.isAuthenticated) {
    // 修复登录审查#15：携带 redirect，登录成功后回跳原被拦截页
    // （LoginView 侧仅收站内相对路径，防开放重定向）
    return { path: '/login', query: { redirect: to.fullPath } }
  }

  // 管理员不应访问完善信息页面，自动重定向到管理后台
  if (to.path === '/setup' && hasAdminRole(currentUser?.role)) {
    return '/admin'
  }

  // 修复角色权限审查#2：管理后台总闸——非 admin/super_admin 一律 403。
  // 此前 /admin/** 的可入性完全依赖每页 meta.permission 是否在权限表里登记，
  // 漏登记一个码（或兜底判定放宽一档）就等于开出一条免检通道：页面能渲染、接口全 403。
  if (to.path.startsWith('/admin') && !hasAdminRole(currentUser?.role)) {
    return { path: '/403', query: { redirect: from.fullPath } }
  }

  // 超管专属页面：仅 super_admin 可入，其余角色（含持有同名权限码的组织管理员）跳 /403
  if (to.meta.superAdminOnly === true && currentUser?.role !== 'super_admin') {
    return { path: '/403', query: { redirect: from.fullPath } }
  }

  // Unified permission check using meta.permission
  const permissionCode = to.meta.permission as string | undefined
  if (permissionCode && currentUser) {
    // SUPER_ADMIN 拥有所有权限，直接放行
    if (currentUser.role === 'super_admin') {
      return true
    }

    // 使用统一权限引擎检查
    const { hasPermission } = useUnifiedPermission()
    if (!hasPermission(permissionCode)) {
      // 无权限 → 跳转到 403 页面（保留来源以便返回）
      return { path: '/403', query: { redirect: from.fullPath } }
    }
  }

  // 智能分析全量修复：meta.permissions（any-of 数组）——后端 @PreAuthorize 为
  // hasRole('SUPER_ADMIN') or 双码 or，持有任一码即放行（super_admin 已在上层短路）。
  // 与侧边栏/入口按钮统一为「双码 any-of」口径，消除此前四种门控口径并存的分裂。
  const permissionCodes = to.meta.permissions as string[] | undefined
  if (permissionCodes?.length && currentUser && currentUser.role !== 'super_admin') {
    const { hasPermission } = useUnifiedPermission()
    if (!permissionCodes.some((code) => hasPermission(code))) {
      return { path: '/403', query: { redirect: from.fullPath } }
    }
  }

  return true
}
